// Eksport kartoteki klientów (26.10.2026): wybór cech, podgląd pliku i adres pobrania.
//
// Lustro listy pól z `Login/backend/lib/clientExport.js#POLA`: ta sama kolejność (= kolejność
// kolumn w pliku) i te same nagłówki, bo podgląd w oknie ma pokazywać dokładnie plik, który
// przyjdzie z serwera. Zgodność pilnuje `Login/backend/tests/clientExport.test.js`, który czyta
// ten plik, dlatego każde pole stoi w JEDNEJ linii. `header` to nagłówek kolumny w pliku (po
// polsku, jak w PDF-ach), a podpis pola w oknie idzie przez `t()` (`clientsExport.field.<key>`).
// Te napisy to DANE pliku (lustro renderera serwera, kategoria (2) w CLAUDE.md, „i18n — co zostaje
// po polsku”), dlatego plik ma wpis w scripts/i18n-baseline.json i nie schodzi do zera.
export const EXPORT_FIELDS = [
  { key: 'firstname', header: 'Imię', group: 'person' },
  { key: 'lastname', header: 'Nazwisko', group: 'person' },
  { key: 'email', header: 'E-mail', group: 'person' },
  { key: 'phone_number', header: 'Telefon', group: 'person' },
  { key: 'address', header: 'Adres', group: 'address' },
  { key: 'street', header: 'Ulica', group: 'address' },
  { key: 'building_number', header: 'Nr budynku', group: 'address' },
  { key: 'apartment_number', header: 'Nr lokalu', group: 'address' },
  { key: 'postal_code', header: 'Kod pocztowy', group: 'address' },
  { key: 'city', header: 'Miejscowość', group: 'address' },
  { key: 'client_type', header: 'Rodzaj klienta', group: 'company' },
  { key: 'company_name', header: 'Nazwa firmy', group: 'company' },
  { key: 'company_nip', header: 'NIP', group: 'company' },
  { key: 'company_regon', header: 'REGON', group: 'company' },
  { key: 'registration_date', header: 'Data dodania', group: 'history' },
  { key: 'reservations_count', header: 'Liczba rezerwacji', group: 'history' },
  { key: 'last_reservation', header: 'Ostatnia rezerwacja', group: 'history' },
];

export const EXPORT_GROUPS = ['person', 'address', 'company', 'history'].map((id) => ({
  id,
  fields: EXPORT_FIELDS.filter((f) => f.group === id).map((f) => f.key),
}));

export const DEFAULT_FIELDS = ['firstname', 'lastname', 'email', 'phone_number'];
export const EXPORT_FORMATS = ['xlsx', 'csv'];

const KLUCZE = new Set(EXPORT_FIELDS.map((f) => f.key));

/** Wybrane pola w kolejności kolumn pliku, bez nieznanych kluczy. */
export function orderedFields(selected) {
  const s = new Set(selected || []);
  return EXPORT_FIELDS.filter((f) => s.has(f.key));
}

/** 'all' | 'some' | 'none' — stan pola wyboru całej grupy. */
export function groupState(selected, group) {
  const s = new Set(selected || []);
  const ile = group.fields.filter((k) => s.has(k)).length;
  if (ile === 0) return 'none';
  return ile === group.fields.length ? 'all' : 'some';
}

/** Zaznacza całą grupę, a gdy jest już cała zaznaczona — odznacza ją. */
export function toggleGroup(selected, group) {
  const s = new Set(selected || []);
  const wszystkie = groupState(selected, group) === 'all';
  group.fields.forEach((k) => (wszystkie ? s.delete(k) : s.add(k)));
  return EXPORT_FIELDS.map((f) => f.key).filter((k) => s.has(k));
}

export function toggleField(selected, key) {
  const s = new Set(selected || []);
  if (s.has(key)) s.delete(key); else s.add(key);
  return EXPORT_FIELDS.map((f) => f.key).filter((k) => s.has(k));
}

/** Parametry `GET /api/clients/export`. `scope: 'list'` niesie wyszukiwanie, filtr i sortowanie. */
export function exportQuery({ format, fields, scope, q, filter, sort }) {
  const p = new URLSearchParams();
  p.set('format', EXPORT_FORMATS.includes(format) ? format : 'xlsx');
  p.set('fields', orderedFields(fields).map((f) => f.key).join(','));
  if (scope === 'list') {
    p.set('scope', 'list');
    if (q && q.trim()) p.set('q', q.trim());
    if (filter && filter !== 'all') p.set('filter', filter);
    if (sort) p.set('sort', sort);
  }
  return p.toString();
}

// Dzień z wartości z API: „2026-10-26”, „2026-10-26T00:00:00.000Z” albo obiekt Date.
function dzien(v) {
  if (!v) return '';
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
  }
  const s = String(v);
  // Data z serwera bywa północą czasu polskiego zapisaną w UTC (…T22:00:00.000Z): dzień liczony
  // lokalnie, jak w backendzie, inaczej podgląd pokazywałby dzień wcześniej niż plik.
  if (/T\d{2}:\d{2}/.test(s)) return dzien(new Date(s));
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function adres(c) {
  const numer = [c.building_number, c.apartment_number ? `/${c.apartment_number}` : ''].join('').trim();
  const lewa = [c.street, numer].filter(Boolean).join(' ').trim();
  const prawa = [c.postal_code, c.city].filter(Boolean).join(' ').trim();
  return [lewa, prawa].filter(Boolean).join(', ') || c.address || '';
}

const WARTOSC = {
  address: adres,
  client_type: (c) => (c.client_type === 'company' ? 'Firma' : 'Osoba prywatna'),
  registration_date: (c) => dzien(c.registration_date),
  last_reservation: (c) => dzien(c.last_reservation),
  reservations_count: (c) => String(Number(c.reservations_count) || 0),
};

/** Wartość komórki tak, jak trafi do pliku. */
export function cellValue(client, key) {
  if (!KLUCZE.has(key)) return '';
  const f = WARTOSC[key];
  const v = f ? f(client) : client[key];
  return v == null ? '' : String(v);
}

/** Pierwsze `n` wierszy podglądu: tablice wartości w kolejności wybranych kolumn. */
export function previewRows(clients, selected, n = 3) {
  const pola = orderedFields(selected);
  return (clients || []).slice(0, n).map((c) => pola.map((f) => cellValue(c, f.key)));
}

/** Litera kolumny arkusza: 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(i) {
  let n = i + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Nazwa pliku z nagłówka Content-Disposition, z zapasem. */
export function filenameFromDisposition(header, fallback) {
  const m = String(header || '').match(/filename="?([^";]+)"?/i);
  return m ? m[1] : fallback;
}

// Ostatni wybór (cechy, format) pamiętany w przeglądarce: wygoda jednego użytkownika, nie dane.
const KLUCZ_PREFERENCJI = 'clientsExport:v1';

export function loadExportPrefs(storage) {
  try {
    const s = storage || window.localStorage;
    const raw = JSON.parse(s.getItem(KLUCZ_PREFERENCJI) || 'null');
    const fields = Array.isArray(raw?.fields) ? raw.fields.filter((k) => KLUCZE.has(k)) : [];
    return {
      fields: fields.length ? orderedFields(fields).map((f) => f.key) : [...DEFAULT_FIELDS],
      format: EXPORT_FORMATS.includes(raw?.format) ? raw.format : 'xlsx',
    };
  } catch (_) {
    return { fields: [...DEFAULT_FIELDS], format: 'xlsx' };
  }
}

export function saveExportPrefs({ fields, format }, storage) {
  try {
    const s = storage || window.localStorage;
    s.setItem(KLUCZ_PREFERENCJI, JSON.stringify({ fields, format }));
  } catch (_) { /* tryb prywatny albo zablokowane dane strony: wybór nie zostanie zapamiętany */ }
}
